<template>
  <ApolloMutation
    :mutation="CreateSupportTicketQuery"
    :variables="form"
    :context="{
      hasUpload: true
    }"
    @done="result => {
      clearFormDataFromStore();
      $router.push({name: 'tickets.edit', params: {id: result.data.createSupportTicket}})
    }"
  >
    <template #default="{mutate, loading, error}">
      <p v-if="error">
        {{ $t('An error occurred') }}: {{ error }}
      </p>

      <el-container class="main-box">
        <el-header>
          <el-row class="title-part-first-row">
            <el-col :span="24">
              <h1>{{ $t("helpdesk.newTicket.mainTitle") }}</h1>
            </el-col>
          </el-row>

          <el-divider class="title-bottom-line" />
        </el-header>

        <el-main
          v-if="userIsRepresentant"
          class="access-denied-message-box"
        >
          <i class="el-icon-lock" />

          <h1>{{ $t('helpdesk.accessDenied.title') }}</h1>

          <h2>{{ $t('helpdesk.accessDenied.message') }}</h2>

          <el-button
            type="primary"
            size="mini"
            style="width: fit-content;"
            @click="$router.push({name: 'tickets'})"
          >
            {{ $t('helpdesk.accessDenied.dashboard') }}
          </el-button>
        </el-main>

        <el-form
          ref="ticketForm"
          v-loading="loading"
          :model="form"
          :rules="rules"
          label-width="140px"
          class="new-ticket-form"
          :class="userIsRepresentant ? 'disabled-ticket-form' : ''"
        >
          <el-main>
            <br>
            <el-container class="details-box">
              <el-form-item
                :label="$t('helpdesk.newTicket.subjectLabel')"
                prop="subject"
              >
                <el-input v-model="form.subject" />
              </el-form-item>

              <el-form-item
                :label="$t('helpdesk.newTicket.descriptionLabel')"
                prop="description"
              >
                <el-input
                  ref="descriptionInput"
                  v-model="form.description"
                  type="textarea"
                  resize="vertical"
                  :rows="6"
                  :autosize="{minRows: 7, maxRows: 10}"
                />
              </el-form-item>

              <el-form-item
                :label="$t('helpdesk.newTicket.tagsLabel')"
                prop="tags"
              >
                <el-select
                  v-model="form.tags"
                  class="full-width"
                  multiple
                  filterable
                  remote
                  reserve-keyword
                  :remote-method="fetchTags"
                  :loading="isLoadingTags"
                  :placeholder="$t('helpdesk.newTicket.tagsPlaceholder')"
                >
                  <el-option
                    v-for="item in tagsOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>

              <el-form-item
                :label="$t('helpdesk.newTicket.typeOfSupportLabel')"
                prop="type"
                class="text-left"
              >
                <el-select
                  v-model="form.type"
                  clearable
                  :placeholder="$t('helpdesk.newTicket.typeOfSupportPlaceholder')"
                  class="full-width"
                  @clear="form.ref=''"
                  @change="refLabel(form.type)"
                >
                  <el-option
                    v-for="item in typeOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>

              <el-form-item
                v-show="form.type"
                :label="$t(refNumberLabel).toString()"
                prop="ref"
              >
                <el-select
                  v-model="form.ref"
                  filterable
                  remote
                  reserve-keyword
                  clearable
                  allow-create
                  :placeholder="$t('helpdesk.newTicket.idPlaceholder')"
                  :remote-method="searchRef"
                  :loading="searchRefLoader"
                  style="width:100%"
                >
                  <el-option
                    v-for="item in refOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>

              <el-form-item
                :label="$t('Attachments')"
                prop="attachment"
                style="text-align: start;"
              >
                <el-upload
                  ref="attachmentsUpload"
                  class="attachment-upload"
                  action="#"
                  :auto-upload="false"
                  drag
                  multiple
                  :on-change="(file) => form.attachments.push(file.raw)"
                  :on-remove="handleRemove"
                >
                  <i class="el-icon-upload" />
                  <div class="el-upload__text">
                    {{ $t('Drop file here or') }} <em>{{ $t('click to upload') }}</em>
                  </div>
                </el-upload>
              </el-form-item>
            </el-container>
            <el-form-item class="form-buttons">
              <el-button
                :disabled="loading || !isFormValid"
                type="danger"
                class="create-button"
                @click="mutate()"
              >
                {{ $t('helpdesk.newTicket.createTicketButtonText') }}
              </el-button>
              <el-button
                class="cancel-button"
                @click="navigateBack"
              >
                {{ $t('Cancel') }}
              </el-button>
            </el-form-item>
          </el-main>
          {{ refNumberValidationMessage }}
        </el-form>
      </el-container>
    </template>
  </ApolloMutation>
</template>

<script lang="ts">
import { Form } from 'element-ui'
import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'
import {
  CreateSupportTicketQuery,
  SupportTicketType,
  MutationCreateSupportTicketArgs,
  GetAvailableTicketsTagsQuery,
  SearchTicketOrderRefsQuery,
  Maybe,
  AvailableSupportTicketTag,
  UserRole
} from '@/types/graphql'
import TicketFormModule from '@/store/modules/ticket-form'
import { getModule } from 'vuex-module-decorators'
import UserModule from '@/store/modules/user'
@Component({
  components: {}
})
export default class extends Vue {
  private CreateSupportTicketQuery=CreateSupportTicketQuery
  private form: MutationCreateSupportTicketArgs ={
    subject: '',
    description: '',
    tags: [],
    type: null,
    ref: '',
    attachments: []
  }

  private tagsOptions: AvailableSupportTicketTag[] = []
  private searchRefLoader=false;
  private refOptions=[] as {label: string, value: string}[]

  private ticketFormModule = getModule(TicketFormModule, this.$store);
  private userModule = getModule(UserModule, this.$store);

  private get userIsRepresentant() {
    return this.userModule.roles.includes(UserRole.Representant)
  }

  private isLoadingTags=false;
  private isFormValid=false;

  private validateSubject = (rule: any, value: string, callback: Function) => {
    if (!value) {
      return callback(new Error(this.$t('helpdesk.newTicket.subjectValidationMessage').toString()))
    } else {
      callback()
    }
  }

  private validateDescription = (rule: any, value: string, callback: Function) => {
    if (!value) {
      return callback(new Error(this.$t('helpdesk.newTicket.descriptionValidationMessage').toString()))
    } else {
      callback()
    }
  }

  private validateRef = (rule: any, value: string, callback: Function) => {
    if (!value && this.form.type && this.refLabel(this.form.type) === 'packageNumberLabel') {
      return callback(new Error(this.$t('helpdesk.newTicket.packageNumberValidationMessage').toString()))
    }
    if (!value && this.form.type && this.refLabel(this.form.type) === 'packageItemSerialNumberLabel') {
      return callback(new Error(this.$t('helpdesk.newTicket.packageItemSerialNumberValidationMessage').toString()))
    }
    setTimeout(() => {
      callback()
    }, 500)
  }

  private rules = {
    subject: [{ required: true, validator: this.validateSubject, trigger: 'blur' }],
    description: [{ required: true, validator: this.validateDescription, trigger: 'blur' }],
    ref: [{ required: true, validator: this.validateRef, trigger: 'blur' }]
  }

  @Watch('form', { deep: true })
  private validateForm() {
    (this.$refs.ticketForm as Form).validate(async(valid) => (this.isFormValid = valid))
  }

  private get typeOptions() {
    return [{
      value: SupportTicketType.HgvPackageItemTicket,
      label: this.$t('helpdesk.newTicket.typeOptions.hgvPackageItemTicket').toString()
    }, {
      value: SupportTicketType.HgvPackageTicket,
      label: this.$t('helpdesk.newTicket.typeOptions.hgvPackageTicket').toString()
    }, {
      value: SupportTicketType.LibertPackageItemTicket,
      label: this.$t('helpdesk.newTicket.typeOptions.libertPackageItemTicket').toString()
    }, {
      value: SupportTicketType.LibertPackageTicket,
      label: this.$t('helpdesk.newTicket.typeOptions.libertPackageTicket').toString()
    }, {
      value: SupportTicketType.SiemensPackageItemTicket,
      label: this.$t('helpdesk.newTicket.typeOptions.siemensPackageItemTicket').toString()
    }, {
      value: SupportTicketType.SiemensPackageTicket,
      label: this.$t('helpdesk.newTicket.typeOptions.siemensPackageTicket').toString()
    }]
  }

  private handleRemove(file: any) {
    const index = this.form.attachments!.indexOf(file.raw, 0)
    if (index > -1) {
      this.form.attachments!.splice(index, 1)
    }
  }

  private navigateBack() {
    this.$router.push({ name: 'tickets' })
  }

  private fetchTags(query: string) {
    this.isLoadingTags = true
    this.$apollo.query({ query: GetAvailableTicketsTagsQuery, variables: { search: query } }).then((
      { data }
    ) => {
      this.tagsOptions = data.getAvailableTicketsTags ? data.getAvailableTicketsTags : []
    }).catch(err => {
      console.error(err)
      return []
    })
      .finally(() => {
        this.isLoadingTags = false
      })
  }

  mounted() {
    this.fetchTags('')
    this.refLabel(this.form.type)
    this.fillFormFromStore()
  }

  private refNumberLabel = ''
  private refNumberValidationMessage = ''

  private refLabel(type: Maybe<SupportTicketType> | undefined) {
    if (type) {
      if (type.indexOf('PACKAGE_ITEM_TICKET') === -1) {
        this.refNumberLabel = 'helpdesk.newTicket.packageNumberLabel'
        return 'packageNumberLabel'
      } else {
        this.refNumberLabel = 'helpdesk.newTicket.packageItemSerialNumberLabel'
        return 'packageItemSerialNumberLabel'
      }
    }
  }

  @Watch('$i18n.locale')
  private languageChanged() {
    (this.$refs.ticketForm as Form).validate(async(valid) => (this.isFormValid = valid))
  }

  private searchRef(input: string) {
    this.searchRefLoader = true
    this.$apollo.query({
      query: SearchTicketOrderRefsQuery,
      variables: {
        type: this.form.type,
        search: input
      }
    }).then(({ data: { searchTicketOrderRefs } }) => {
      this.refOptions = searchTicketOrderRefs.map((item: string) => {
        return {
          label: item,
          value: item
        }
      })
    }).finally(() => {
      this.searchRefLoader = false
    })
  }

  focusTextareaAtMotif() {
    if (this.$refs.descriptionInput) {
      const textarea = (this.$refs.descriptionInput as Vue).$el.querySelector('textarea')

      if (textarea) {
        textarea.focus()

        const text = textarea.value
        const motifIndex = text.indexOf('Motif:')

        if (motifIndex !== -1) {
          const cursorPosition = motifIndex + 'Motif:'.length + 1

          textarea.setSelectionRange(cursorPosition, cursorPosition)

          textarea.scrollTop = textarea.scrollHeight
        }
      }
    }
  }

  private fillFormFromStore() {
    const packageNumberFromRoute = this.$route.params.packageNumber
    const packageNumberFromStore = this.ticketFormModule.packageNumber

    if (packageNumberFromRoute === packageNumberFromStore) {
      this.form.ref = this.ticketFormModule.packageNumber

      const packageType = this.ticketFormModule.packageType

      switch (packageType) {
        case 'Badge PASSANGO':
          this.form.type = SupportTicketType.HgvPackageTicket
          break
        case 'Badge LiberT':
          this.form.type = SupportTicketType.LibertPackageTicket
          break
        case 'OBU PASSANGO':
          this.form.type = SupportTicketType.SiemensPackageTicket
          break
      }

      if (this.ticketFormModule.subject) {
        this.form.subject = this.ticketFormModule.subject
      }

      if (this.ticketFormModule.description) {
        this.form.description = this.ticketFormModule.description
      }

      if (this.ticketFormModule.tag) {
        this.form.tags = [this.ticketFormModule.tag]
      }

      this.$nextTick(() => {
        this.focusTextareaAtMotif()
      })
    }
  }

  private clearFormDataFromStore() {
    this.ticketFormModule.resetState()
  }
}
</script>

<style lang="scss">
  @import "@/css/ticketCreateEdit.scss";
</style>
